import { ClientStatus, PaymentStatus, SubscriptionStatus, AlertSeverity, AlertStatus } from '../../types';
import { cn } from '../../lib/utils';

interface StatusBadgeProps {
  status: ClientStatus | PaymentStatus | SubscriptionStatus | AlertSeverity | AlertStatus | string;
  type?: 'client' | 'payment' | 'subscription' | 'severity' | 'alert' | 'autoRenew' | 'source';
  dot?: boolean;
}

const statusColors: Record<string, string> = {
  // Client status
  active: 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
  'at-risk': 'bg-red-500/15 text-red-700 dark:bg-red-500/20 dark:text-red-400',
  churned: 'bg-gray-500/15 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400',
  'pending-migration': 'bg-amber-500/15 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',

  // Payment status
  current: 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
  flagged: 'bg-red-500/15 text-red-700 dark:bg-red-500/20 dark:text-red-400',
  suspended: 'bg-gray-500/15 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400',

  // Subscription status
  'pending-cancellation': 'bg-orange-500/15 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
  cancelled: 'bg-gray-500/15 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400',

  // Auto-renew status
  'auto-renew-on': 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
  'auto-renew-off': 'bg-orange-500/15 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
  'unknown': 'bg-gray-500/15 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400',

  // Source badges
  'pax8': 'bg-blue-500/15 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
  'direct': 'bg-orange-500/15 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
  'manual': 'bg-purple-500/15 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',

  // Subscription category badges (trial/free)
  'trial': 'bg-gray-500/10 text-gray-500 dark:bg-gray-500/15 dark:text-gray-400',
  'free': 'bg-gray-500/10 text-gray-500 dark:bg-gray-500/15 dark:text-gray-400',

  // Alert severity
  critical: 'bg-red-500/15 text-red-700 dark:bg-red-500/20 dark:text-red-400',
  warning: 'bg-orange-500/15 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
  info: 'bg-blue-500/15 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',

  // Alert status
  acknowledged: 'bg-amber-500/15 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
  resolved: 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
  snoozed: 'bg-purple-500/15 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',
};

const dotColors: Record<string, string> = {
  active: 'bg-emerald-500',
  current: 'bg-emerald-500',
  'auto-renew-on': 'bg-emerald-500',
  resolved: 'bg-emerald-500',
  'at-risk': 'bg-red-500',
  flagged: 'bg-red-500',
  critical: 'bg-red-500',
  'pending-cancellation': 'bg-orange-500',
  'auto-renew-off': 'bg-orange-500',
  warning: 'bg-orange-500',
  info: 'bg-blue-500',
  pax8: 'bg-blue-500',
};

const statusLabels: Record<string, string> = {
  'at-risk': 'At Risk',
  'pending-migration': 'Pending Migration',
  'pending-cancellation': 'Pending Cancellation',
  'auto-renew-on': 'Recurring: On',
  'auto-renew-off': 'Recurring: Off',
  'unknown': 'Unknown',
  'pax8': 'Pax8',
  'direct': 'MS Direct',
  'manual': 'Manual',
  'trial': 'Trial',
  'free': 'Free',
};

export default function StatusBadge({ status, dot = false }: StatusBadgeProps) {
  const colorClass = statusColors[status] || 'bg-gray-500/15 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400';
  const label = statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium",
        colorClass
      )}
    >
      {dot && (
        <span className={cn(
          "h-1.5 w-1.5 rounded-full",
          dotColors[status] || 'bg-gray-400'
        )} />
      )}
      {label}
    </span>
  );
}
